import * as React from 'react'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import { cn } from '@/lib/utils'

interface ResponsiveModalProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

interface ResponsiveModalContentProps {
  className?: string
  children: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
}

interface ResponsiveModalHeaderProps {
  className?: string
  children: React.ReactNode
}

interface ResponsiveModalTitleProps {
  className?: string
  children: React.ReactNode
}

interface ResponsiveModalDescriptionProps {
  className?: string
  children: React.ReactNode
}

interface ResponsiveModalCloseProps {
  className?: string
  children: React.ReactNode
  asChild?: boolean
}

interface ResponsiveModalTriggerProps {
  className?: string
  children: React.ReactNode
  asChild?: boolean
}

const ResponsiveModalContext = React.createContext<{
  isMobile: boolean
}>({ isMobile: false })

const useResponsiveModal = () => {
  const context = React.useContext(ResponsiveModalContext)
  if (!context) {
    throw new Error('useResponsiveModal must be used within ResponsiveModal')
  }
  return context
}

export const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  open,
  onOpenChange,
  children,
}) => {
  const isMobile = useMobileMediaQuery()

  return (
    <ResponsiveModalContext.Provider value={{ isMobile }}>
      {isMobile ? (
        <Sheet open={open} onOpenChange={onOpenChange}>
          {children}
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {children}
        </Dialog>
      )}
    </ResponsiveModalContext.Provider>
  )
}

export const ResponsiveModalContent: React.FC<ResponsiveModalContentProps> = ({
  className,
  children,
  side = 'bottom',
}) => {
  const { isMobile } = useResponsiveModal()

  if (isMobile) {
    return (
      <SheetContent
        side={side}
        className={cn(
          'max-md:flex max-md:flex-col max-md:h-auto max-md:rounded-t-xl',
          className,
        )}
      >
        {children}
      </SheetContent>
    )
  }

  return <DialogContent className={className}>{children}</DialogContent>
}

export const ResponsiveModalHeader: React.FC<ResponsiveModalHeaderProps> = ({
  className,
  children,
}) => {
  const { isMobile } = useResponsiveModal()

  if (isMobile) {
    return <SheetHeader className={className}>{children}</SheetHeader>
  }

  return <DialogHeader className={className}>{children}</DialogHeader>
}

export const ResponsiveModalTitle: React.FC<ResponsiveModalTitleProps> = ({
  className,
  children,
}) => {
  const { isMobile } = useResponsiveModal()

  if (isMobile) {
    return <SheetTitle className={className}>{children}</SheetTitle>
  }

  return <DialogTitle className={className}>{children}</DialogTitle>
}

export const ResponsiveModalDescription: React.FC<
  ResponsiveModalDescriptionProps
> = ({ className, children }) => {
  const { isMobile } = useResponsiveModal()

  if (isMobile) {
    return <SheetDescription className={className}>{children}</SheetDescription>
  }

  return <DialogDescription className={className}>{children}</DialogDescription>
}

export const ResponsiveModalClose: React.FC<ResponsiveModalCloseProps> = ({
  className,
  children,
  asChild,
}) => {
  const { isMobile } = useResponsiveModal()

  if (isMobile) {
    return (
      <SheetClose className={className} asChild={asChild}>
        {children}
      </SheetClose>
    )
  }

  return (
    <DialogClose className={className} asChild={asChild}>
      {children}
    </DialogClose>
  )
}

export const ResponsiveModalTrigger: React.FC<ResponsiveModalTriggerProps> = ({
  className,
  children,
  asChild,
}) => {
  const { isMobile } = useResponsiveModal()

  if (isMobile) {
    return (
      <SheetTrigger className={className} asChild={asChild}>
        {children}
      </SheetTrigger>
    )
  }

  return (
    <DialogTrigger className={className} asChild={asChild}>
      {children}
    </DialogTrigger>
  )
}
